/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 08-09-2026
 *
 * De opgeslagen stand van het document plus zijn undo- en redo-geschiedenis. Puur: geen api, geen
 * timers, geen sessie — deze klasse weet alleen wat de huidige stand is en wat de vorige waren.
 * Alles wat een backendcall, een timer of een guard nodig heeft staat in `OpslagService`, die deze
 * klasse bezit (het patroon van `ModelSerialisatie` en `SimResultaatTekenaar`: een gewone klasse,
 * niet injecteerbaar).
 *
 * De stand wordt bij het in- en uitgaan gekopieerd. In AngularJS deed `clone()` dat met
 * `angular.merge({}, obj)`; hier is het een JSON-rondgang, en dat is precies de vorm die ook over de
 * lijn gaat en in een snapshot belandt. Zonder die kopie deelt de graaf zijn `data`- en
 * `position`-objecten met de bewaarde stand — de reden waarom de ng1-code het al deed.
 */
import type {ModelDocument, ModelMeta} from './model-serialisatie';

/**
 * Hoeveel undostappen we bewaren. Even groot als de ng1-`maxUndo`.
 */
const MAX_UNDO = 200;

export class Documentstand {

    /**
     * De huidige stand: het laatst ingelezen of opgeslagen document.
     */
    private stand: ModelDocument = Documentstand.leegDocument();

    /**
     * De bewaarde vorige standen, oudste eerst; de laatste is waar een undo naartoe gaat.
     */
    private undoStack: ModelDocument[] = [];

    /**
     * De standen die door een undo zijn verlaten; de laatste is waar een redo naartoe gaat.
     */
    private redoStack: ModelDocument[] = [];

    /**
     * Een leeg document, zoals een nieuw model begint. De zoom is 0.5 omdat het tekenen daar bij een
     * ontbrekende waarde ook op terugvalt, en de conditiesets en toon-vlaggen staan op dezelfde
     * defaults als waarop `ModelSerialisatie.lees` terugvalt — een leeg document tekent dus precies
     * zoals het altijd deed.
     */
    public static leegDocument(): ModelDocument {
        return {
            cy: {zoom: 0.5},
            nodes: {},
            model: {
                elements: {},
                types: {}
            },
            condtags: {},
            condtag: null,
            bsToonNoodzakelijk: true,
            bsToonGewenst: false,
            normToon: true,
            simToon: false
        };
    }

    /**
     * De huidige stand, als losse kopie.
     */
    public data(): ModelDocument {
        return this.kopie(this.stand);
    }

    /**
     * De huidige stand zelf, zonder kopie. Alleen voor lezers die er niets in schrijven en er niets
     * van bewaren — de normtelling en de normmatch, die er elke wijziging opnieuw overheen lopen.
     */
    public ruweData(): ModelDocument {
        return this.stand;
    }

    /**
     * Vervangt de stand door een kopie van het gegeven document.
     */
    public zet(doc: ModelDocument): void {
        this.stand = this.kopie(doc);
    }

    /**
     * Vervangt de stand door het gegeven document zonder te kopiëren: het document is al van ons,
     * omdat het net uit json is uitgepakt en verder nergens vandaan komt.
     */
    public neemOver(doc: ModelDocument): void {
        this.stand = doc;
    }

    /**
     * Zet de metadata op de huidige stand. Die hoort niet bij wat `ModelSerialisatie.schrijf`
     * schrijft (een snapshot heeft hem niet), maar wel bij elk opgeslagen model.
     */
    public addMeta(meta: ModelMeta): void {
        this.stand.meta = meta;
    }

    /**
     * Zet de stand terug op een leeg document, voor een nieuw model.
     */
    public reset(): void {
        this.stand = Documentstand.leegDocument();
    }

    /**
     * Bewaart de huidige stand als undopunt. Aan te roepen vóór een logische wijziging: het is de
     * stand waar je naartoe terug wilt. Een nieuwe wijziging maakt redo onmogelijk.
     */
    public setUndoPoint(): void {
        this.undoStack.push(this.kopie(this.stand));
        this.redoStack = [];
        while (this.undoStack.length > MAX_UNDO) {
            this.undoStack.shift(); // de oudste eruit
        }
    }

    /**
     * Of er een stand is om naar terug te gaan.
     */
    public canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    /**
     * Of er een ge-undo'de stand is om naar terug te gaan.
     */
    public canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    /**
     * Gaat terug naar de vorige stand en zet de huidige op de redo-stapel. Geeft `false` als er
     * niets te undo'en valt.
     */
    public undo(): boolean {
        const vorige = this.undoStack.pop();
        if (!vorige) {
            return false;
        }
        this.redoStack.push(this.kopie(this.stand));
        this.stand = vorige;
        return true;
    }

    /**
     * Gaat terug naar de stand van vóór de undo en zet de huidige op de undo-stapel — zonder
     * `setUndoPoint`, want de redo-stapel blijft geldig. Geeft `false` als er niets te redo'en valt.
     */
    public redo(): boolean {
        const volgende = this.redoStack.pop();
        if (!volgende) {
            return false;
        }
        this.undoStack.push(this.kopie(this.stand));
        this.stand = volgende;
        return true;
    }

    /**
     * Gooit de hele geschiedenis weg, voor een nieuw of opnieuw geladen model.
     */
    public wisGeschiedenis(): void {
        this.undoStack = [];
        this.redoStack = [];
    }

    /**
     * Het aantal bewaarde undo- en redostappen, voor logging en tests.
     */
    public diepte(): {undo: number; redo: number} {
        return {undo: this.undoStack.length, redo: this.redoStack.length};
    }

    /**
     * Een losse kopie van een document: de vorm die ook over de lijn gaat.
     */
    private kopie(doc: ModelDocument): ModelDocument {
        return JSON.parse(JSON.stringify(doc)) as ModelDocument;
    }
}
